import React, { useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardContent, Typography, Box, Grid, Chip, IconButton, Switch } from "@mui/material";
import { Refresh, PlayArrow, Pause, Stop } from "@mui/icons-material";
import { fetchRealtimeAnalyticsApi } from "../../services/enhancedDashboard";
import { toastMessage } from "../../utils/toastMessage";

const RealTimeCharts = ({ data }) => {
  const [isLive, setIsLive] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds
  const intervalRef = useRef(null);
  const [chartData, setChartData] = useState({
    liveActivity: {
      series: [{ name: "Active Users", data: [] }],
      options: {
        chart: {
          type: "area",
          height: 250,
          animations: {
            enabled: true,
            easing: "linear",
            speed: 800,
          },
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
          },
        },
        xaxis: {
          type: "datetime",
          range: 300000, // 5 minutes
        },
        yaxis: { title: { text: "Active Users" } },
        title: { text: "Live User Activity", align: "left" },
        colors: ["#00acc1"],
        grid: {
          borderColor: "#f1f1f1",
        },
      },
    },
    liveRevenue: {
      series: [{ name: "Revenue", data: [] }],
      options: {
        chart: {
          type: "line",
          height: 250,
          animations: {
            enabled: true,
            easing: "linear",
            speed: 800,
          },
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 4 },
        xaxis: {
          type: "datetime",
          range: 300000, // 5 minutes
        },
        yaxis: { title: { text: "Revenue (₹)" } },
        title: { text: "Live Revenue Stream", align: "left" },
        colors: ["#4caf50"],
        grid: {
          borderColor: "#f1f1f1",
        },
      },
    },
    liveSessions: {
      series: [{ name: "Active Sessions", data: [] }],
      options: {
        chart: {
          type: "bar",
          height: 250,
          animations: {
            enabled: true,
            easing: "linear",
            speed: 800,
          },
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: { columnWidth: "70%" },
        },
        xaxis: {
          type: "datetime",
          range: 300000, // 5 minutes
        },
        yaxis: { title: { text: "Sessions" } },
        title: { text: "Live Session Count", align: "left" },
        colors: ["#ff9800"],
        grid: {
          borderColor: "#f1f1f1",
        },
      },
    },
    liveConsultants: {
      series: [{ name: "Online Consultants", data: [] }],
      options: {
        chart: {
          type: "line",
          height: 250,
          animations: {
            enabled: true,
            easing: "linear",
            speed: 800,
          },
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        markers: { size: 4 },
        xaxis: {
          type: "datetime",
          range: 300000, // 5 minutes
        },
        yaxis: { title: { text: "Consultants" } },
        title: { text: "Online Consultants", align: "left" },
        colors: ["#9c27b0"],
        grid: {
          borderColor: "#f1f1f1",
        },
      },
    },
  });

  const [liveStats, setLiveStats] = useState({
    activeUsers: 0,
    onlineConsultants: 0,
    activeSessions: 0,
    revenueToday: 0,
    peakUsers: 0,
    avgResponseTime: 0,
  });

  // Generate random data for simulation
  const generateRandomData = () => {
    const now = new Date();
    const baseUsers = 150;
    const baseConsultants = 25;
    const baseSessions = 45;
    const baseRevenue = 5000;

    return {
      users: Math.floor(baseUsers + (Math.random() - 0.5) * 50),
      consultants: Math.floor(baseConsultants + (Math.random() - 0.5) * 10),
      sessions: Math.floor(baseSessions + (Math.random() - 0.5) * 20),
      revenue: Math.floor(baseRevenue + (Math.random() - 0.5) * 2000),
    };
  };

  // Update live data
  const updateLiveData = () => {
    const newData = generateRandomData();
    const now = new Date();

    setLiveStats(prev => ({
      activeUsers: newData.users,
      onlineConsultants: newData.consultants,
      activeSessions: newData.sessions,
      revenueToday: prev.revenueToday + newData.revenue,
      peakUsers: Math.max(prev.peakUsers, newData.users),
      avgResponseTime: Math.floor(Math.random() * 10) + 10,
    }));

    // Update chart data
    setChartData(prev => ({
      liveActivity: {
        ...prev.liveActivity,
        series: [{
          name: "Active Users",
          data: [...prev.liveActivity.series[0].data.slice(-20), {
            x: now.getTime(),
            y: newData.users,
          }],
        }],
      },
      liveRevenue: {
        ...prev.liveRevenue,
        series: [{
          name: "Revenue",
          data: [...prev.liveRevenue.series[0].data.slice(-20), {
            x: now.getTime(),
            y: newData.revenue,
          }],
        }],
      },
      liveSessions: {
        ...prev.liveSessions,
        series: [{
          name: "Active Sessions",
          data: [...prev.liveSessions.series[0].data.slice(-20), {
            x: now.getTime(),
            y: newData.sessions,
          }],
        }],
      },
      liveConsultants: {
        ...prev.liveConsultants,
        series: [{
          name: "Online Consultants",
          data: [...prev.liveConsultants.series[0].data.slice(-20), {
            x: now.getTime(),
            y: newData.consultants,
          }],
        }],
      },
    }));
  };

  // Start/stop live updates
  useEffect(() => {
    if (isLive) {
      intervalRef.current = setInterval(updateLiveData, refreshInterval);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isLive, refreshInterval]);

  // Initialize with some data
  useEffect(() => {
    const initialData = generateRandomData();
    setLiveStats({
      activeUsers: initialData.users,
      onlineConsultants: initialData.consultants,
      activeSessions: initialData.sessions,
      revenueToday: initialData.revenue,
      peakUsers: initialData.users,
      avgResponseTime: 15,
    });
  }, []);

  const liveStatsCards = [
    {
      title: "Active Users",
      value: liveStats.activeUsers,
      change: `Peak: ${liveStats.peakUsers}`,
      color: "#00acc1",
      icon: "👥",
    },
    {
      title: "Online Consultants",
      value: liveStats.onlineConsultants,
      change: "Live",
      color: "#9c27b0",
      icon: "👨‍💼",
    },
    {
      title: "Active Sessions",
      value: liveStats.activeSessions,
      change: "Real-time",
      color: "#ff9800",
      icon: "💬",
    },
    {
      title: "Revenue Today",
      value: `₹${liveStats.revenueToday.toLocaleString()}`,
      change: "Live",
      color: "#4caf50",
      icon: "💰",
    },
    {
      title: "Avg. Response Time",
      value: `${liveStats.avgResponseTime} min`,
      change: "Live",
      color: "#f44336",
      icon: "⏱️",
    },
  ];

  return (
    <Box>
      {/* Live Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {liveStatsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="h2" color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Chip
                      label={stat.change}
                      size="small"
                      color={stat.change === "Live" || stat.change === "Real-time" ? "success" : "default"}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Box color={stat.color} fontSize="2rem">
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Live Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Real-time Monitoring</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2">Live Updates:</Typography>
              <Switch
                checked={isLive}
                onChange={(e) => setIsLive(e.target.checked)}
                color="primary"
              />
              <IconButton
                onClick={() => updateLiveData()}
                color="primary"
                disabled={isLive}
              >
                <Refresh />
              </IconButton>
              <Typography variant="body2" color="textSecondary">
                Refresh: {refreshInterval / 1000}s
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Live Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.liveActivity.options}
                series={chartData.liveActivity.series}
                type="area"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.liveRevenue.options}
                series={chartData.liveRevenue.series}
                type="line"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.liveSessions.options}
                series={chartData.liveSessions.series}
                type="bar"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={chartData.liveConsultants.options}
                series={chartData.liveConsultants.series}
                type="line"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Status */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Chip
                  label="API Status"
                  color="success"
                  icon={<PlayArrow />}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  All systems operational
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Chip
                  label="Database"
                  color="success"
                  icon={<PlayArrow />}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Connection stable
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Chip
                  label="WebSocket"
                  color={isLive ? "success" : "default"}
                  icon={isLive ? <PlayArrow /> : <Pause />}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {isLive ? "Live updates active" : "Updates paused"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Chip
                  label="Performance"
                  color="success"
                  icon={<PlayArrow />}
                />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Response time: 120ms
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RealTimeCharts;

